import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Disc3, Play, Pause, Clock, Zap, Music, Trash2, Key } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import AudioPlayer from '@/components/AudioPlayer';
import AudioWaveform from '@/components/AudioWaveform';
import useRestorePlayer from '@/utils/useRestorePlayer';
import { usePlayer } from '@/context/PlayerContext';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export default function MixesPage() {
  useDocumentTitle('Mixes');
  const [mixes, setMixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMix, setExpandedMix] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const hasFetched = useRef(false);

  // Audio player state — owned by global PlayerContext now.
  const { playingTrack: playingMix, isPlaying, playTrack, closePlayer, seek, audioElRef } = usePlayer();

  // Track the currently-playing mix's playback position so we can render
  // a live progress line on its waveform. We subscribe directly to the
  // <audio> element (via context) so this doesn't cause re-renders on
  // pages that don't care about time.
  useEffect(() => {
    if (!playingMix?.id || !audioElRef?.current) {
      setCurrentTime(0);
      return;
    }
    const audio = audioElRef.current;
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('seeked', onTime);
    // Prime once on mount / track change
    onTime();
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('seeked', onTime);
    };
  }, [playingMix?.id, audioElRef]);

  // Auto-restore the player after iOS Safari kills the page on long locks.
  useRestorePlayer(mixes, playingMix, () => {});

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchMixes();
  }, []);

  const fetchMixes = async () => {
    try {
      const response = await axios.get(`${API}/mixes`);
      setMixes(response.data.mixes || []);
    } catch (error) {
      toast.error('Failed to load mixes');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (mix) => {
    playTrack(mix, [mix]);
  };

  const handleDelete = async (mixId) => {
    try {
      await axios.delete(`${API}/mixes/${mixId}`);
      setMixes(prev => prev.filter(m => m.id !== mixId));
      if (playingMix?.id === mixId) {
        closePlayer();
      }
      toast.success('Mix deleted');
    } catch (error) {
      toast.error('Failed to delete mix');
    }
  };

  const getEnergyColor = (energy) => {
    if (energy >= 75) return 'text-[#2563FF]';
    if (energy >= 50) return 'text-yellow-400';
    if (energy >= 25) return 'text-green-400';
    return 'text-blue-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="mixes-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 data-page-header="redundant" className="font-heading text-3xl sm:text-4xl font-bold mb-2 text-[#0F172A] tracking-tight">
              Your <span className="gradient-text">mixes</span>
            </h1>
            <p className="font-body text-[#64748B] text-sm">
              Long-form DJ sets and recorded mixes ({mixes.length})
            </p>
          </div>
        </div>

        {mixes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center mx-auto mb-6">
              <Disc3 className="w-10 h-10 text-[#1E3A8A]" />
            </div>
            <h3 className="font-heading text-xl text-[#64748B] mb-2">No mixes yet</h3>
            <p className="font-body text-[#94A3B8] text-sm max-w-md mx-auto mb-6">
              Upload audio files longer than 10 minutes via the Upload page and they will automatically appear here as mixes.
            </p>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 btn-primary px-6 py-3"
              data-testid="upload-mix-btn"
            >
              Upload a Mix
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {mixes.map((mix) => {
              const analysis = mix.analysis || {};
              const isExpanded = expandedMix === mix.id;
              const isCurrentlyPlaying = playingMix?.id === mix.id && isPlaying;
              const isActiveMix = playingMix?.id === mix.id;
              const mixDuration = mix.duration || 0;
              const mixProgress = isActiveMix && mixDuration > 0
                ? Math.max(0, Math.min(1, currentTime / mixDuration))
                : 0;
              const handleSeek = (fraction) => {
                if (!mix.has_audio) return;
                if (!isActiveMix) {
                  // Not yet loaded — start playback first, then seek once
                  // metadata is ready. Play with the seek fraction remembered.
                  playTrack(mix, [mix]);
                  const target = fraction * mixDuration;
                  const audio = audioElRef?.current;
                  if (audio && Number.isFinite(target)) {
                    const onReady = () => {
                      audio.removeEventListener('loadedmetadata', onReady);
                      try { audio.currentTime = target; } catch { /* ignore */ }
                    };
                    audio.addEventListener('loadedmetadata', onReady, { once: true });
                  }
                  return;
                }
                seek(fraction * mixDuration);
              };

              return (
                <motion.div
                  key={mix.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-[#E2E8F0] hover:border-[#E2E8F0] transition-colors"
                  data-testid={`mix-card-${mix.id}`}
                >
                  {/* Main row */}
                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                    {/* Play button */}
                    <button
                      onClick={() => handlePlay(mix)}
                      disabled={!mix.has_audio}
                      className="w-12 h-12 sm:w-14 sm:h-14 bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0 hover:bg-[#EFF6FF] transition-colors disabled:opacity-30"
                      data-testid={`play-mix-${mix.id}`}
                    >
                      {isCurrentlyPlaying ? (
                        <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-[#2563FF]" />
                      ) : (
                        <Play className="w-5 h-5 sm:w-6 sm:h-6 text-[#2563FF] ml-0.5" />
                      )}
                    </button>

                    {/* Info */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpandedMix(isExpanded ? null : mix.id)}
                    >
                      <h3 className="font-heading text-sm sm:text-base text-[#0F172A] truncate">{mix.title}</h3>
                      <p className="font-body text-xs text-[#64748B] truncate">{mix.artist || 'Unknown Artist'}</p>
                      {mix.waveform_peaks && mix.waveform_peaks.length > 0 && (
                        <div className="mt-2 mr-4 hidden sm:block" data-testid={`mix-waveform-${mix.id}`} onClick={(e) => e.stopPropagation()}>
                          <AudioWaveform
                            peaks={mix.waveform_peaks}
                            height={28}
                            progress={mixProgress}
                            onSeek={handleSeek}
                            structure={analysis.structure}
                            duration={mixDuration}
                          />
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="hidden sm:flex items-center gap-4">
                      <div className="text-center">
                        <p className="font-mono text-sm text-[#2563FF]">{analysis.bpm ? Math.round(analysis.bpm) : '--'}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase">BPM</p>
                      </div>
                      <div className="text-center">
                        <p className="font-mono text-sm text-blue-400">{analysis.camelot_code || analysis.camelot || '--'}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Key</p>
                      </div>
                      <div className="text-center">
                        <p className={`font-mono text-sm ${getEnergyColor(analysis.energy)}`}>{analysis.energy || '--'}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Energy</p>
                      </div>
                      <div className="text-center">
                        <p className="font-mono text-sm text-[#64748B]">{formatDuration(mix.duration)}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Duration</p>
                      </div>
                    </div>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(mix.id)}
                      className="p-2 text-[#94A3B8] hover:text-[#2563FF] transition-colors"
                      data-testid={`delete-mix-${mix.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Mobile stats row */}
                  <div className="sm:hidden flex items-center gap-4 px-3 pb-3">
                    <span className="font-mono text-xs text-[#2563FF]">{analysis.bpm ? Math.round(analysis.bpm) : '--'} BPM</span>
                    <span className="font-mono text-xs text-blue-400">{analysis.camelot_code || analysis.camelot || '--'}</span>
                    <span className={`font-mono text-xs ${getEnergyColor(analysis.energy)}`}>E:{analysis.energy || '--'}</span>
                    <span className="font-mono text-xs text-[#64748B]">{formatDuration(mix.duration)}</span>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-[#E2E8F0] p-4">
                      {mix.waveform_peaks && mix.waveform_peaks.length > 0 && (
                        <div className="mb-4" data-testid={`mix-waveform-full-${mix.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Waveform</p>
                            {isActiveMix && mixDuration > 0 && (
                              <p className="font-mono text-[10px] text-[#64748B]">
                                {formatDuration(currentTime)} / {formatDuration(mixDuration)}
                              </p>
                            )}
                          </div>
                          <AudioWaveform
                            peaks={mix.waveform_peaks}
                            height={72}
                            progress={mixProgress}
                            onSeek={handleSeek}
                            structure={analysis.structure}
                            duration={mixDuration}
                          />
                          {/* Section legend — only when structure present */}
                          {analysis.structure && analysis.structure.length > 0 && (
                            <div className="flex flex-wrap gap-3 mt-2">
                              {[
                                ['drop',      '#F97316'],
                                ['build',     '#F59E0B'],
                                ['main',      '#2563FF'],
                                ['breakdown', '#8B5CF6'],
                                ['verse',     '#0EA5E9'],
                                ['intro/outro', '#94A3B8'],
                              ]
                                .filter(([type]) => {
                                  if (type === 'intro/outro') {
                                    return analysis.structure.some(s => s.type === 'intro' || s.type === 'outro');
                                  }
                                  return analysis.structure.some(s => s.type === type);
                                })
                                .map(([type, hex]) => (
                                  <span key={type} className="flex items-center gap-1.5 font-mono text-[10px] text-[#64748B]">
                                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: hex }} />
                                    {type}
                                  </span>
                                ))}
                            </div>
                          )}
                          <p className="font-mono text-[10px] text-[#94A3B8] mt-1.5">Click any bar to jump the playhead</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-1">BPM</p>
                          <p className="font-mono text-lg text-[#2563FF]">{analysis.bpm ? analysis.bpm.toFixed(1) : '--'}</p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-1">Key</p>
                          <p className="font-mono text-lg text-blue-400">{analysis.camelot_code || analysis.camelot || '--'}</p>
                          {analysis.key && <p className="font-mono text-xs text-[#64748B]">{analysis.key}</p>}
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-1">Energy</p>
                          <p className={`font-mono text-lg ${getEnergyColor(analysis.energy)}`}>{analysis.energy || '--'}<span className="text-xs text-[#94A3B8]">/100</span></p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-1">Duration</p>
                          <p className="font-mono text-lg text-[#334155]">{formatDuration(mix.duration)}</p>
                        </div>
                      </div>

                      {/* Structure sections */}
                      {analysis.structure && analysis.structure.length > 0 && (
                        <div>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-2">Structure ({analysis.structure.length} sections)</p>
                          <div className="flex gap-0.5 h-6 mb-2">
                            {analysis.structure.map((section, i) => {
                              const width = mix.duration ? ((section.end - section.start) / mix.duration) * 100 : 0;
                              const colors = {
                                intro: 'bg-gray-700', verse: 'bg-gray-600',
                                build: 'bg-yellow-700', drop: 'bg-[#2563FF]',
                                outro: 'bg-gray-700', breakdown: 'bg-blue-800'
                              };
                              return (
                                <div
                                  key={i}
                                  className={`${colors[section.type] || 'bg-gray-700'} relative group`}
                                  style={{ width: `${Math.max(width, 0.5)}%` }}
                                  title={`${section.type} (${formatDuration(section.start)} - ${formatDuration(section.end)})`}
                                />
                              );
                            })}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {analysis.structure.map((section, i) => (
                              <span key={i} className="font-mono text-[10px] text-[#64748B]">
                                {section.type} ({formatDuration(section.start)})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Drop count */}
                      {analysis.drops && analysis.drops.length > 0 && (
                        <div className="mt-3">
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-1">
                            Drops: {analysis.drops.length}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {analysis.drops.map((drop, i) => (
                              <span key={i} className="bg-[#EFF6FF] border border-[#E2E8F0] px-2 py-0.5 font-mono text-[10px] text-[#2563FF]">
                                {formatDuration(drop.start)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* View full detail */}
                      <div className="mt-4">
                        <Link
                          to={`/track/${mix.id}`}
                          className="font-mono text-xs text-[#2563FF] hover:text-[#3B82F6] transition-colors"
                        >
                          View full analysis &rarr;
                        </Link>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audio Player is now mounted globally in App.js via GlobalAudioPlayer */}
    </div>
  );
}
